import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Shield, Check, Phone, Mail, FileDown, Upload, X, FileText, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Warranty = () => {
  const [customContent, setCustomContent] = useState(null);
  const [warrantyPdf, setWarrantyPdf] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/settings`);
        if (res.data.warranty_content) {
          setCustomContent(res.data.warranty_content);
        }
      } catch (e) {
        console.error('Failed to fetch settings:', e);
      }
    };
    
    const fetchWarrantyPdf = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/warranty/pdf`);
        if (res.data.has_pdf) {
          setWarrantyPdf(res.data);
        }
      } catch (e) {
        console.error('Failed to fetch warranty PDF:', e);
      }
    };
    
    const checkAdminStatus = () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          if (payload.role === 'admin' || payload.role === 'superadmin') {
            setIsAdmin(true);
          }
        }
      } catch (e) {
        // Not logged in or invalid token
      }
    };
    
    fetchSettings();
    fetchWarrantyPdf();
    checkAdminStatus();
  }, []);

  const handlePdfUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please select a PDF file');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_URL}/api/warranty/upload-pdf`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      
      setWarrantyPdf({
        has_pdf: true,
        file_url: res.data.file_url,
        filename: res.data.filename
      });
      toast.success('Warranty PDF uploaded successfully');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to upload PDF');
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePdf = async () => {
    if (!confirm('Are you sure you want to delete the warranty PDF?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/warranty/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setWarrantyPdf(null);
      toast.success('Warranty PDF deleted');
    } catch (e) {
      toast.error('Failed to delete PDF');
    }
  };

  return (
    <div className="min-h-screen bg-[#f0f4f8]">
      <Navbar />
      
      {/* Hero Section */}
      <div className="pt-44 pb-12 bg-gradient-to-br from-[#003456] to-[#001d30]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="w-20 h-20 bg-[#fdc31c] rounded-full flex items-center justify-center mx-auto mb-6">
            <Shield className="h-10 w-10 text-[#003456]" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Warranty Information</h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto mb-6">
            Your HomeLyph freeze dryer is built to last. We stand behind our products with comprehensive warranty coverage for customers in <strong>mainland Canada</strong> and <strong>continental USA</strong>.
          </p>
          
          {/* Download Warranty PDF Button */}
          {warrantyPdf && warrantyPdf.has_pdf && (
            <a 
              href={`${API_URL}${warrantyPdf.file_url}`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#fdc31c] hover:bg-[#e5b019] text-[#0a0203] font-semibold px-6 py-3 rounded-lg transition-colors"
              data-testid="download-warranty-pdf"
            >
              <FileDown className="h-5 w-5" />
              Download Full Warranty (PDF)
            </a>
          )}
        </div>
      </div>
      
      {/* Admin PDF Upload Section */}
      {isAdmin && (
        <div className="max-w-4xl mx-auto px-6 mt-8">
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <FileText className="h-5 w-5 text-amber-700" />
              </div>
              <div>
                <h3 className="font-semibold text-amber-900">Admin: Warranty PDF Management</h3>
                <p className="text-sm text-amber-700">Upload or replace the warranty PDF document</p>
              </div>
            </div>
            
            {warrantyPdf && warrantyPdf.has_pdf ? (
              <div className="flex items-center justify-between bg-white rounded-lg p-4 border border-amber-200">
                <div className="flex items-center gap-3">
                  <FileText className="h-8 w-8 text-red-500" />
                  <div>
                    <p className="font-medium text-gray-900">{warrantyPdf.filename || 'warranty.pdf'}</p>
                    <a 
                      href={`${API_URL}${warrantyPdf.file_url}`} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-sm text-blue-600 hover:underline"
                    >
                      View PDF
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <label className="cursor-pointer">
                    <input 
                      type="file" 
                      accept=".pdf" 
                      onChange={handlePdfUpload}
                      className="hidden" 
                      data-testid="warranty-pdf-replace-input"
                    />
                    <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium">
                      <Upload className="h-4 w-4" />
                      Replace
                    </span>
                  </label>
                  <Button 
                    onClick={handleDeletePdf}
                    variant="outline"
                    className="border-red-300 text-red-600 hover:bg-red-50"
                    data-testid="warranty-pdf-delete-btn"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <label className="cursor-pointer block">
                <input 
                  type="file" 
                  accept=".pdf" 
                  onChange={handlePdfUpload}
                  className="hidden" 
                  data-testid="warranty-pdf-upload-input"
                />
                <div className="border-2 border-dashed border-amber-300 rounded-lg p-8 text-center hover:border-amber-500 transition-colors bg-white">
                  {uploading ? (
                    <div className="flex flex-col items-center gap-2">
                      <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-500 border-t-transparent"></div>
                      <p className="text-amber-700">Uploading...</p>
                    </div>
                  ) : (
                    <>
                      <Upload className="h-10 w-10 text-amber-500 mx-auto mb-3" />
                      <p className="font-medium text-amber-900">Click to upload warranty PDF</p>
                      <p className="text-sm text-amber-600 mt-1">PDF files up to 10MB</p>
                    </>
                  )}
                </div>
              </label>
            )}
          </div>
        </div>
      )}
      
      <div className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          {customContent ? (
            <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12 prose prose-lg max-w-none">
              <div className="text-gray-600" dangerouslySetInnerHTML={{ __html: customContent }} />
            </div>
          ) : (
            <>
          {/* Warranty Highlights */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              { title: '2-Year', subtitle: 'Limited Warranty', description: 'Covers manufacturing defects' },
              { title: '1-Year', subtitle: 'Vacuum Pump', description: 'Full coverage on pump components' },
              { title: '1-Year', subtitle: 'Accessories', description: 'Trays, seals, and accessories' }
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 text-center shadow-sm">
                <p className="text-4xl font-bold text-[#003456]">{item.title}</p>
                <p className="text-lg font-medium text-[#0a0203] mb-2">{item.subtitle}</p>
                <p className="text-gray-500 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
          
          {/* Warranty Details */}
          <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12">
            <h2 className="text-2xl font-bold text-[#0a0203] mb-6">Warranty Coverage Details</h2>
            
            <div className="space-y-8">
              <div className="bg-[#dbeef5] rounded-xl p-6">
                <h3 className="text-lg font-semibold text-[#003456] mb-2">Coverage for Mainland Canada & Continental USA</h3>
                <p className="text-gray-600">
                  This warranty applies to all HomeLyph freeze dryers purchased and used in <strong>mainland Canada</strong> or the <strong>continental United States of America</strong>. 
                  Warranty service is available in both countries through our authorized service network.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-[#003456] mb-3">What's Covered</h3>
                <ul className="space-y-3">
                  {[
                    'Manufacturing defects in materials and workmanship',
                    'Freeze dryer chamber and door assembly',
                    'Refrigeration system components',
                    'Electronic control systems',
                    'Vacuum pump (1-year coverage)',
                    'Factory-installed accessories'
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-[#003456] mb-3">What's Not Covered</h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• Damage caused by misuse, abuse, or unauthorized modifications</li>
                  <li>• Normal wear and tear (gaskets, seals, trays)</li>
                  <li>• Damage from power surges or improper electrical connections</li>
                  <li>• Use with incorrect voltage (110V in USA/Canada standard; verify your local requirements)</li>
                  <li>• Cosmetic damage that doesn't affect functionality</li>
                  <li>• Damage during shipping (covered by shipping insurance)</li>
                  <li>• Commercial use (separate warranty terms apply – contact us for commercial warranty)</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-[#003456] mb-3">Warranty Service by Region</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-[#0a0203] mb-2">Mainland Canada</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Service available in all provinces</li>
                      <li>• Parts shipped from Canadian warehouse</li>
                      <li>• English language support</li>
                    </ul>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-[#0a0203] mb-2">Continental United States</h4>
                    <ul className="text-sm text-gray-600 space-y-1">
                      <li>• Service available in continental USA</li>
                      <li>• Parts shipped from US distribution center</li>
                      <li>• English language support</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-[#003456] mb-3">How to Make a Warranty Claim</h3>
                <ol className="space-y-3 text-gray-600">
                  <li className="flex gap-3">
                    <span className="w-6 h-6 bg-[#003456] text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">1</span>
                    <span>Contact our support team at <a href="mailto:support@homelyph.com" className="text-[#003456] hover:underline">support@homelyph.com</a> or through your <a href="https://freezedryersupport.com" target="_blank" rel="noopener noreferrer" className="text-[#003456] hover:underline">Customer Portal</a></span>
                  </li>
                  <li className="flex gap-3">
                    <span className="w-6 h-6 bg-[#003456] text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">2</span>
                    <span>Provide your <strong>serial number</strong>, <strong>model type</strong>, country of purchase, and describe the issue</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="w-6 h-6 bg-[#003456] text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">3</span>
                    <span>Our team will diagnose the issue and provide repair or replacement instructions</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="w-6 h-6 bg-[#003456] text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">4</span>
                    <span>We'll ship replacement parts from the nearest warehouse (Canada or US) or arrange for local repair service</span>
                  </li>
                </ol>
              </div>

              <div className="bg-[#f0f4f8] rounded-xl p-6">
                <h3 className="text-xl font-semibold text-[#003456] mb-3">Extended Warranty Options</h3>
                <p className="text-gray-600 mb-4">
                  Extend your peace of mind with our optional extended warranty coverage, available to customers in both Canada and the United States. 
                  Contact us within 30 days of purchase to add extended coverage to your freeze dryer.
                </p>
                <Link to="/contact">
                  <Button className="bg-[#003456] hover:bg-[#001d30] text-white">
                    Contact Us About Extended Warranty
                  </Button>
                </Link>
              </div>
            </div>
          </div>
            </>
          )}
          
          {/* Contact Section */}
          <div className="mt-12 bg-[#003456] rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">Need Warranty Support?</h2>
            <p className="text-white/80 mb-6">Our team is here to help you with any warranty questions or claims.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="mailto:support@homelyph.com">
                <Button className="bg-[#fdc31c] hover:bg-[#e5b019] text-[#0a0203]">
                  <Mail className="h-4 w-4 mr-2" />
                  Email Support
                </Button>
              </a>
              <a href="https://freezedryersupport.com" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#003456]">
                  Customer Portal
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Warranty;
